import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  isSignal,
  output,
  signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  DeepSignal,
  IUiListSettings,
  ListSettings,
  PropExtend,
  UTILS,
} from '@aksia/infrastructure';
import {
  CdkDragDrop,
  CdkDragPlaceholder,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { IconComponent } from '../../presentation/icon/icon.component';
import { LabelComponent } from '../../presentation/label/label.component';
import { UiControlDirective } from '../../../directives/ui/ui-control.directive';

@Component({
  selector: 'list',
  imports: [DragDropModule, NgTemplateOutlet, IconComponent, LabelComponent],
  templateUrl: './list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListComponent<T = unknown> extends UiControlDirective {
  uiHeaderTemplate = input<TemplateRef<any>>();
  uiItemTemplate = input<TemplateRef<any>>();
  uiItemOptionsTemplate = input<TemplateRef<any>>();

  labelCssClass = input<string>('');
  wrapperCssClass = input<string>('');
  itemCssClass = input<string>('');
  actionCssClass = input<string>('');
  reOrder = output<any[]>();
  itemClicked = output<T>();

  protected itemsFromSettings = signal<Array<unknown>>([]);
  reOrdering = computed(
    () => this.settings().reOrdering && !!this.settings().itemSortingKey,
  );

  override settings = input(new ListSettings(), {
    transform: (settings: IUiListSettings<T>) => this.initSettings(settings),
  });

  currentId = signal(-1);
  itemIdChange = output<number>();
  rawOptionMap = new Map<unknown, unknown>();

  protected override initSettings(settings: IUiListSettings<T>) {
    let listSettings = UTILS.OBJECT.assign(new ListSettings(), settings);

    super.initSettings(listSettings);

    if (isSignal(settings.items)) {
      this.itemsFromSettings = listSettings.items;
    } else if (settings.items instanceof Function) {
      this.itemsFromSettings.set(listSettings.items());
    } else {
      this.itemsFromSettings.set(listSettings.items);
    }

    return listSettings;
  }

  protected plainItems = computed(() => this._computePlainItems());

  private _computePlainItems() {
    let plainItems = this.itemsFromSettings()?.map(
      (option: PropExtend<unknown>) => {
        let optionPlain: PropExtend<unknown> = UTILS.SIGNALS.isDeepSignal(
          option,
        )
          ? isSignal(option)
            ? (
                option as WritableSignal<
                  DeepSignal<unknown & { _plain: unknown }>
                >
              )?.()?._plain()
            : (option as DeepSignal<unknown & { _plain: unknown }>)?._plain()
          : isSignal(option)
            ? option()
            : option;

        this.rawOptionMap.set(optionPlain, option);
        return optionPlain;
      },
    );

    if (!!this.settings().itemSortingKey) {
      let sortField = this.settings().itemSortingKey;
      return plainItems?.sort((a: any, b: any) =>
        a[sortField] > b[sortField] ? 1 : -1,
      );
    }

    return plainItems;
  }
  toggleListItemOptions(id: number) {
    this.currentId.set(Number(id));
    this.itemIdChange.emit(this.currentId());
  }

  drop(event: CdkDragDrop<any[]>) {
    const allItems = this.itemsFromSettings();

    // Helper to check if item is hidden
    const isHidden = (item: unknown): boolean =>
      typeof item === 'object' &&
      item !== null &&
      'hidden' in item &&
      (item as any).hidden;

    // Get only visible items
    const visibleItems = allItems.filter((item) => !isHidden(item));

    // Find the item being moved in visible list
    const itemToMove = visibleItems[event.previousIndex];
    if (!itemToMove) return;

    // Find the indexes in the original array
    const fromIndex = allItems.indexOf(itemToMove);
    const toItem = visibleItems[event.currentIndex];
    const toIndex = allItems.indexOf(toItem);

    this.itemsFromSettings.update((items) => {
      // Remove and re-insert item in the original array
      items.splice(fromIndex, 1);
      items.splice(toIndex, 0, itemToMove);

      // Reassign sorting indexes for visible items only
      items.forEach((item, index) => {
        if (UTILS.SIGNALS.isDeepSignal(item)) {
          (item as any)()[this.settings().itemSortingKey]?.set(index);
        } else if (isSignal(item)) {
          (item as any).update((obj: any) => {
            obj[this.settings().itemSortingKey] = index;
            return { ...obj };
          });
        } else {
          (item as any)[this.settings().itemSortingKey] = index;
        }
      });

      return [...items];
    });

    this.reOrder.emit(this.plainItems());
  }

  onItemClicked(item: T, event: PointerEvent) {
    let el = event.target as HTMLElement;
    if (el.closest('[uilistStopPropagation]')) {
      return;
    }
    this.itemClicked.emit(item);
  }
}
